<script setup lang="ts">
  import * as yup from 'yup'
  import { useField, useForm } from 'vee-validate'
  import { FormInput, Loading } from '@components/index'
  import { mutateForgotPassword, mutateResetPassword } from '@server/index'
  import successToast from '@/toasts/successToast'
  import navigate from '@utils/navigate'
  import handleError from '@utils/errorHandler'

  const loading = ref(false)

  const emailSubmitted = ref(false)

  const toast = useToast()

  definePageMeta({
    layout: 'auth'
  })

  const hash = ref('')

  const validationSchemaRecovery = yup.object({
    email: yup.string().required('Email é obrigatório').email().label('Email Inválido')
  })

  const {
    handleSubmit: handleSubmitRecovery,
    errors: errorsRecovery,
    validate: validateRecovery
  } = useForm({
    validationSchema: validationSchemaRecovery,
    initialValues: {
      email: ''
    }
  })

  const { value: emailField } = useField<string>('email')

  const submitButtonText = ref('ENVIAR')
  const recoveryControl = handleSubmitRecovery(async () => {
    loading.value = true
    const { execute, data, error } = mutateForgotPassword({
      email: emailField.value
    })

    try {
      await execute()
      if (error.value) {
        handleError(error.value)
      }
      if (data.value) {
        hash.value = data.value
        emailSubmitted.value = true
        successToast({ toast, description: 'Enviamos um código para seu e-mail.', showIcon: false })
      }
    } finally {
      setTimeout(() => {
        loading.value = false
      }, 800)
    }
  })

  const handleRecovery = async () => {
    await validateRecovery()
    recoveryControl()
  }

  watch(emailField, () => {
    errorsRecovery.value.email = ''
  })

  const { value: passwordtokenField } = useField<string>('text')
  const { value: passwordField } = useField<string>('password')

  const validationSchemaReset = yup.object({
    password: yup
      .string()
      .required('Senha é obrigatória')
      .min(8, 'Senha deve ter pelo menos 6 caracteres')
      .label('Senha inválida'),
    passwordtoken: yup
      .string()
      .required('O token é obrigatório')
      .length(6, 'O token possui 6 digitos')
      .label('Token inválida')
  })

  const {
    handleSubmit: handleSubmitReset,
    errors: errorsReset,
    validate: validateReset
  } = useForm({
    validationSchema: validationSchemaReset,
    initialValues: {
      passwordtoken: '',
      password: ''
    }
  })

  const resetControl = handleSubmitReset(async () => {
    loading.value = true
    const { execute, data } = mutateResetPassword({
      token: hash.value,
      newPassword: passwordField.value,
      code: parseInt(passwordtokenField.value)
    })
    try {
      await execute()
      if (data.value) {
        navigate.push(`/auth`)
        successToast({ toast, description: 'Senha alterada com sucesso!' })
      }
    } finally {
      setTimeout(() => {
        loading.value = false
      }, 800)
    }
  })

  const handleReset = async () => {
    await validateReset()
    resetControl()
  }

  watch(emailField, () => {
    errorsReset.value.passwordtoken = ''
  })

  watch(passwordField, () => {
    errorsReset.value.password = ''
  })

  const submitController = () => {
    if (!emailSubmitted.value) {
      handleRecovery()
    } else {
      handleReset()
    }
  }
</script>

<template>
  <div>
    <div>
      <form
        v-if="!emailSubmitted"
        :validation-schema="validationSchemaRecovery"
        @submit.prevent="handleRecovery"
      >
        <div>
          <div>
            <FormInput
              id="email"
              v-model="emailField"
              class="col-12 form-input-component mb-3"
              label="E-mail"
              name="email"
              type="email"
              icon="bi bi-envelope-fill"
              :is-invalid="!!errorsRecovery.email"
            />
            {{ errorsRecovery.email }}
            <div class="ml-2 text-center">
              <p style="margin-bottom: 0px">
                Um código de recuperação será enviado em seu e-mail de acesso fornecido
              </p>
            </div>
          </div>
        </div>
      </form>
      <form
        v-else
        :validation-schema="validationSchemaRecovery"
        @submit.prevent="handleRecovery"
      >
        <div class="ml-2 text-center mt-2">
          <p>Digite abaixo o código que você recebeu e a nova senha</p>
        </div>
        <div>
          <div>
            <FormInput
              id="passwordtoken"
              v-model="passwordtokenField"
              class="col-12 form-input-component mb-3"
              label="Código"
              name="passwordtoken"
              type="text"
              icon="bi bi bi-123"
              :is-invalid="!!errorsRecovery.email"
            />
            {{ errorsReset.passwordtoken }}
            <FormInput
              id="password"
              v-model="passwordField"
              class="col-12 form-input-component mb-3"
              label="Nova senha"
              name="password"
              type="password"
              icon="bi bi-lock-fill"
              :is-invalid="!!errorsRecovery.email"
            />
          </div>
        </div>
      </form>
    </div>
    <div class="form-input-component">
      <button
        data-test-id="login-button"
        class="secondary"
        @click="submitController"
      >
        {{ !loading ? submitButtonText : '' }}
        <Loading
          v-if="loading"
          class="mt-3"
          :color="'#fff'"
          :duration="'2.3s'"
        />
      </button>

      <div class="col-12 text-center mt-4 mb-5">
        <p
          class="recovery-password"
          style="color: #c3c3c6"
        >
          <u class="correct-text-color">
            <NuxtLink to="/auth">Voltar</NuxtLink>
          </u>
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .recovery-password {
    cursor: pointer;
  }
  .ou {
    text-align: center;
    margin-top: -28px;

    span {
      background-color: #000000;
      padding: 0 10px;
    }
  }
  correct-text-color {
    color: #c3c3c6;
  }
</style>
